import { AlertTriangle, Ban, Info } from "lucide-react";
import type { CampaignContact, LeadIssue } from "@/api/email";
import { Hint } from "@/components/email/EmailBadges";
import { cn } from "@/lib/utils";

/** What stands between a lead and its emails, in the backend's own words:
 *  blocking issues first, then warnings. Nothing is inferred here. */
export function LeadIssues({
  row,
  issues,
  max = 2,
}: {
  row: Pick<CampaignContact, "status_reason"> & { issues?: LeadIssue[] };
  issues?: LeadIssue[];
  max?: number;
}) {
  const list = issues ?? row.issues ?? [];
  if (!list.length) {
    if (!row.status_reason) return <span className="text-xs text-slate-300">—</span>;
    return <IssueLine issue={{ code: "STATUS_REASON", message: row.status_reason, blocking: false }} />;
  }
  const ordered = [...list].sort((a, b) => Number(b.blocking) - Number(a.blocking));
  const shown = ordered.slice(0, max);
  const rest = ordered.slice(max);
  return (
    <div className="min-w-0 space-y-0.5">
      {shown.map((issue) => (
        <IssueLine key={`${issue.code}-${issue.message}`} issue={issue} />
      ))}
      {rest.length > 0 && (
        <Hint
          content={
            <span className="block space-y-1">
              {rest.map((issue) => (
                <span key={`${issue.code}-${issue.message}`} className="block">
                  {issue.message}
                </span>
              ))}
            </span>
          }
        >
          <span className="text-[11px] text-slate-400">+{rest.length} more</span>
        </Hint>
      )}
    </div>
  );
}

function IssueLine({ issue }: { issue: LeadIssue }) {
  const Icon = issue.blocking ? Ban : issue.code === "STATUS_REASON" ? Info : AlertTriangle;
  return (
    <Hint content={issue.message}>
      <p
        className={cn(
          "flex min-w-0 items-start gap-1 text-xs leading-snug",
          issue.blocking ? "text-rose-700" : "text-amber-700",
        )}
      >
        <Icon className="mt-px h-3 w-3 shrink-0" />
        <span className="line-clamp-2">{issue.message}</span>
      </p>
    </Hint>
  );
}
